'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import PageHeader from '@/components/ui/PageHeader';

// Galeri kategorileri
const categories = [
  { id: 'all', name: 'Tümü' },
  { id: 'berber-koltuklari', name: 'Berber Koltukları' },
  { id: 'kuafor-tezgahlari', name: 'Kuaför Tezgahları' },
  { id: 'yikama-setleri', name: 'Yıkama Setleri' },
  { id: 'ozel-projeler', name: 'Özel Projeler' },
];

type GalleryItem = {
  id: string;
  title: string;
  imageUrl: string;       // full size
  thumbnailUrl?: string;  // small size
  category: string;
};

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState<null | string>(null);
  const [images, setImages] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      try {
        const res = await fetch('/api/gallery?pageSize=200', { cache: 'no-store' });
        const data = await res.json();
        if (isMounted && data.success && Array.isArray(data.data)) {
          setImages(
            data.data.map((it: any) => ({
              id: it.id,
              title: it.title,
              imageUrl: it.imageUrl,
              thumbnailUrl: it.thumbnailUrl,
              category: it.category,
            }))
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    load();
    return () => { isMounted = false; };
  }, []);

  // Resimleri filtreleme
  const filteredImages = activeCategory === 'all'
    ? images
    : images.filter(image => image.category === activeCategory);

  // Lightbox açma
  const openLightbox = (imageId: string) => {
    setSelectedImage(imageId);
    document.body.style.overflow = 'hidden';
  };

  // Lightbox kapatma
  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  // Önceki resme geçme
  const prevImage = () => {
    if (!selectedImage) return;
    
    const currentIndex = images.findIndex(img => img.id === selectedImage);
    const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setSelectedImage(images[prevIndex]?.id ?? null);
  };

  // Sonraki resme geçme
  const nextImage = () => {
    if (!selectedImage) return;
    
    const currentIndex = images.findIndex(img => img.id === selectedImage);
    const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setSelectedImage(images[nextIndex]?.id ?? null);
  };
  
  // Seçilen resmin detaylarını alma
  const selectedImageDetails = selectedImage 
    ? images.find(img => img.id === selectedImage) 
    : null;

  return (
    <div className="bg-gray-50 py-8 md:py-12">
      <div className="container mx-auto px-4">
        {/* Yeni Page Header */}
        <PageHeader 
          title="Galeri" 
          subtitle="Ürünlerimizin ve projelerimizin fotoğrafları" 
          theme="dark"
        />

        {/* Kategori Filtreleme */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-8 mb-8">
          <div className="flex flex-wrap gap-2 sm:gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm transition-all ${
                  activeCategory === category.id
                    ? 'bg-gray-200 text-primary shadow-md'
                    : 'bg-gray-100 text-primary hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Galeri Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredImages.map((image) => (
            <div
              key={image.id}
              onClick={() => openLightbox(image.id)}
              className="bg-white rounded-lg shadow-sm overflow-hidden cursor-pointer group"
            >
              <div className="relative aspect-square min-h-[208px] overflow-hidden">
                <Image
                  src={image.imageUrl}
                  alt={image.title?.trim() || 'Filiz Kozmetik salon ve proje galeri görseli'}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity"></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-white bg-opacity-80 p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-gray-900 font-medium line-clamp-1">{image.title}</h3>
                <p className="text-gray-500 text-sm">
                  {categories.find(cat => cat.id === image.category)?.name}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Sonuç bulunamadı */}
        {(filteredImages.length === 0 && !loading) && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 13l6-6" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M11 15l-2-2" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900">Bu kategoride henüz görsel bulunmuyor</h3>
            <p className="text-gray-600 mt-2">Lütfen başka bir kategori seçiniz.</p>
          </div>
        )}

        {/* Lightbox */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
            <div className="relative w-full max-w-4xl p-4">
              {/* Kapatma butonu */}
              <button
                onClick={closeLightbox}
                className="absolute top-2 right-2 z-10 p-2 text-white hover:text-primary transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Önceki buton */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Resim */}
              <div className="relative aspect-video w-full overflow-hidden rounded-lg">
                {selectedImageDetails && (
                  <>
                    {/* Bulanık Arka Plan */}
                    <div className="absolute inset-0 overflow-hidden opacity-25 blur-3xl scale-110 pointer-events-none select-none">
                      <Image
                        src={selectedImageDetails.imageUrl}
                        alt=""
                        fill
                        className="object-cover"
                        sizes="30vw"
                        priority
                      />
                      <div className="absolute inset-0 bg-black/40" />
                    </div>
                    <Image
                      src={selectedImageDetails.imageUrl}
                      alt={selectedImageDetails.title?.trim() || 'Filiz Kozmetik galeri görseli'}
                      fill
                      className="object-contain z-10"
                      priority
                    />
                  </>
                )}
              </div>

              {/* Sonraki buton */}
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Resim açıklaması */}
              {selectedImageDetails && (
                <div className="bg-black bg-opacity-70 text-white p-4 mt-2 rounded">
                  <h3 className="text-xl font-semibold">{selectedImageDetails.title}</h3>
                  <p className="text-sm text-gray-300 mt-1">
                    {categories.find(cat => cat.id === selectedImageDetails.category)?.name}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 